import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp, Square } from "lucide-react";
import { cn } from "@/shared/lib/utils";

interface ComposerActionButtonProps {
  /** True while the agent is generating — the button becomes Stop. */
  streaming: boolean;
  /** True when the draft is non-empty (gates the idle Send state). */
  canSubmit: boolean;
  /** Invoked when the button is clicked in the streaming (Stop) state. */
  onStop: () => void;
}

/**
 * The single in-bar action button. Lives absolute-right inside the Composer's
 * input wrapper and crossfades between Send (ArrowUp, idle) and Stop (Square,
 * streaming) so it reads as one element that changes state — not two separate
 * buttons. The Stop affordance lives here (not the StatusBanner) so there is
 * one consistent control whether the agent is idle or running.
 */
export function ComposerActionButton({ streaming, canSubmit, onStop }: ComposerActionButtonProps) {
  return (
    <motion.button
      type={streaming ? "button" : "submit"}
      onClick={streaming ? onStop : undefined}
      disabled={!streaming && !canSubmit}
      aria-label={streaming ? "Stop generating" : "Send question"}
      whileTap={{ scale: 0.92 }}
      className={cn(
        "absolute right-2 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "disabled:cursor-not-allowed disabled:opacity-40",
        streaming
          ? "bg-primary/15 text-primary ring-1 ring-primary/30 hover:bg-primary/25"
          : "bg-primary text-primary-foreground hover:bg-primary/90",
      )}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={streaming ? "stop" : "send"}
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.85 }}
          transition={{ duration: 0.14, ease: [0.4, 0, 0.2, 1] }}
          className="grid place-items-center"
        >
          {streaming ? <Square className="h-4 w-4 fill-current" /> : <ArrowUp className="h-5 w-5" />}
        </motion.span>
      </AnimatePresence>
    </motion.button>
  );
}
